import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { UserPreference } from 'api'
import { PendingCell, PendingContainer, PendingScrollContainer, PendingTable, PendingValidationCell, ValidationStatus } from '../upload'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { PendingMidTradeEventFields } from './validateMidTradeEventParseResult'
import { MidTradeEventAction } from './parseMidTradeEventSpreadsheet'
import { getFeeFormatOptions, getNotionalFormatOptions } from '../datagrid/modelFormatters'
import { HaircutTypeHelper, MidTradeActionHelper, MidTradeResponseHelper } from 'model'
import { formatAsUTCDate } from 'locale/Locale'
import { Locale, Translation } from 'locale'

export interface PendingMidTradeEventPanelProps {
  validMidTradeEventActions: MidTradeEventAction[]
  invalidMidTradeEventActions: MidTradeEventAction[]
  incoming?: boolean
  canUpload: boolean
  handleUpload: () => void
}

type PendingPanelProps = {
  midTradeEventActions: MidTradeEventAction[]
  heading: string
  incoming?: boolean
  userPreferences: UserPreference
}

const mapStateToProps = createStructuredSelector({
  userPreferences: createUserPreferenceSelector()
})

const PendingPanelComponent: React.FunctionComponent<PendingPanelProps> = ({
  midTradeEventActions,
  heading,
  incoming,
  userPreferences
}) => {
  return (
    <>
      <h4>{heading}</h4>
      <PendingScrollContainer>
        <PendingTable>
          <thead>
            <tr>
              <th>{incoming ? Locale.translate(Translation.TC_RESPONSE) : Locale.translate(Translation.TC_ACTION)}</th>
              <th>{Locale.translate(Translation.TC_DEAL_ID)}</th>
              <th>{Locale.translate(Translation.TC_EFFECTIVE_DATE)}</th>
              <th>{Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreferences.amountFormat })}</th>
              <th>{Locale.translate(Translation.TC_RATE)}</th>
              <th>{Locale.translate(Translation.TC_FEE)}</th>
              <th>{Locale.translate(Translation.TC_HAIRCUT)}</th>
              <th>{Locale.translate(Translation.TC_HAIRCUT_TYPE)}</th>
            </tr>
          </thead>
          <tbody>
            {midTradeEventActions.map((midTradeEventAction, idx) => {
              const {
                tradeId,
                effectiveDate,
                notional,
                rate,
                fee,
                haircut,
                haircutType,
                dataIssues,
                actionType,
                responseType
              } = midTradeEventAction

              return (
                <tr key={idx}>
                  <PendingValidationCell
                    validationIssues={dataIssues}
                    fieldName={incoming ? PendingMidTradeEventFields.RESPONSE : PendingMidTradeEventFields.ACTION}
                    rowValidationStatus={dataIssues.length > 0 ? ValidationStatus.ERROR : ValidationStatus.DEFAULT}>
                    {incoming
                      ? responseType && MidTradeResponseHelper.getName(responseType)
                      : actionType && MidTradeActionHelper.getName(actionType)}
                  </PendingValidationCell>
                  <PendingValidationCell validationIssues={dataIssues} fieldName={PendingMidTradeEventFields.TRADE_ID}>
                    {tradeId}
                  </PendingValidationCell>
                  <PendingCell>{formatAsUTCDate(effectiveDate, userPreferences.dateFormat)}</PendingCell>
                  <PendingValidationCell validationIssues={dataIssues} fieldName={PendingMidTradeEventFields.NOTIONAL}>
                    {Locale.formatNumber(notional, getNotionalFormatOptions(userPreferences.amountFormat))}
                  </PendingValidationCell>
                  <PendingCell>{Locale.formatNumber(rate, { percent: true })}</PendingCell>
                  <PendingCell>{Locale.formatNumber(fee, getFeeFormatOptions())}</PendingCell>
                  <PendingValidationCell validationIssues={dataIssues} fieldName={PendingMidTradeEventFields.HAIRCUT}>
                    {Locale.formatNumber(haircut, { percent: true })}
                  </PendingValidationCell>
                  <PendingValidationCell validationIssues={dataIssues} fieldName={PendingMidTradeEventFields.HAIRCUT_TYPE}>
                    {haircutType ? HaircutTypeHelper.getName(haircutType) : null}
                  </PendingValidationCell>
                </tr>
              )
            })}
          </tbody>
        </PendingTable>
      </PendingScrollContainer>
    </>
  )
}

const PendingPanel = connect(mapStateToProps, {})(PendingPanelComponent)

export const PendingMidTradeEventPanel = ({
  validMidTradeEventActions,
  invalidMidTradeEventActions,
  incoming,
  canUpload,
  handleUpload
}: PendingMidTradeEventPanelProps) => {
  return (
    <PendingContainer>
      <h4>{Locale.translate(Translation.TC_PENDING_MID_TRADE_EVENTS)}</h4>
      {validMidTradeEventActions.length > 0 && (
        <>
          <PendingPanel
            heading={Locale.translate(Translation.TC_VALID_MTE, { count: validMidTradeEventActions.length })}
            midTradeEventActions={validMidTradeEventActions}
            incoming={incoming}
          />
          <TextButton
            dataTestId={'AddAssets-UploadAssets-ButtonUpload'}
            type={TextButtonType.Primary}
            disabled={!canUpload}
            onClick={handleUpload}>
            {Locale.translate(Translation.TC_BUTTON_MTE_UPLOAD, { count: validMidTradeEventActions.length })}
          </TextButton>
        </>
      )}
      {invalidMidTradeEventActions.length > 0 && (
        <PendingPanel
          heading={Locale.translate(Translation.TC_INVALID_MTE, { count: invalidMidTradeEventActions.length })}
          midTradeEventActions={invalidMidTradeEventActions}
          incoming={incoming}
        />
      )}
    </PendingContainer>
  )
}
